import React, { useState, useEffect, useMemo } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function startOfMonth(year, month) {
  return new Date(year, month, 1);
}

function addMonths(date, n) {
  return new Date(date.getFullYear(), date.getMonth() + n, 1);
}

function buildMonthGrid(year, month) {
  // Returns 6 weeks x 7 days, week starts Monday
  const firstOfMonth = startOfMonth(year, month);
  const dayOfWeek = (firstOfMonth.getDay() + 6) % 7; // Mon = 0
  const gridStart = new Date(year, month, 1 - dayOfWeek);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }
  return cells;
}

function isoLocalDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseLocalDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

const PALETTE = [
  '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#84cc16', '#0ea5e9', '#f97316',
  '#14b8a6', '#a855f7'
];

function colorForName(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return PALETTE[Math.abs(hash) % PALETTE.length];
}

export default function TeamTimeOffPage({ user }) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [viewDate, setViewDate] = useState(today);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      const start = new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1);
      const end = new Date(viewDate.getFullYear(), viewDate.getMonth() + 2, 0);
      try {
        const res = await fetch(
          `${API_BASE}/api/team/pto_calendar?start=${isoLocalDate(start)}&end=${isoLocalDate(end)}`,
          { credentials: 'include' }
        );
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        const data = await res.json();
        if (!cancelled) setEvents(data.events || []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [viewDate]);

  // Map dateISO -> array of events overlapping that day
  const eventsByDate = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const start = parseLocalDate(ev.start_date);
      const end = parseLocalDate(ev.end_date);
      const cur = new Date(start);
      while (cur <= end) {
        const key = isoLocalDate(cur);
        if (!map[key]) map[key] = [];
        map[key].push(ev);
        cur.setDate(cur.getDate() + 1);
      }
    }
    return map;
  }, [events]);

  // People-out summary for the visible month
  const monthSummary = useMemo(() => {
    const monthStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const monthEnd = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
    const byName = new Map();
    for (const ev of events) {
      if (ev.type === 'holiday') continue;
      const s = parseLocalDate(ev.start_date);
      const e = parseLocalDate(ev.end_date);
      if (e < monthStart || s > monthEnd) continue;
      if (!byName.has(ev.name)) byName.set(ev.name, []);
      byName.get(ev.name).push(ev);
    }
    return Array.from(byName.entries())
      .map(([name, evs]) => ({ name, events: evs }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [events, viewDate]);

  const grid = buildMonthGrid(viewDate.getFullYear(), viewDate.getMonth());
  const monthLabel = `${MONTHS[viewDate.getMonth()]} ${viewDate.getFullYear()}`;

  return (
    <div className="page-wrap">
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1rem',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <h1 className="content-title" style={{ margin: 0 }}>Team Time Off</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={() => setViewDate(addMonths(viewDate, -1))}
            style={navBtnStyle}
            aria-label="Previous month"
          >
            &#8249;
          </button>
          <button
            onClick={() => setViewDate(new Date(today.getFullYear(), today.getMonth(), 1))}
            style={{ ...navBtnStyle, padding: '6px 14px' }}
          >
            Today
          </button>
          <div style={{ minWidth: '180px', textAlign: 'center', fontWeight: 600, fontSize: '1.05rem' }}>
            {monthLabel}
          </div>
          <button
            onClick={() => setViewDate(addMonths(viewDate, 1))}
            style={navBtnStyle}
            aria-label="Next month"
          >
            &#8250;
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fca5a5',
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '12px',
          color: '#7f1d1d'
        }}>
          Failed to load calendar: {error}
        </div>
      )}

      <div className="team-cal-frame" style={{
        background: 'white',
        border: '1px solid var(--symbolica-warm-border)',
        borderRadius: 'var(--radius)',
        overflow: 'hidden',
        minWidth: 0,
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          background: '#F6F4F0',
          borderBottom: '1px solid var(--symbolica-warm-border)'
        }}>
          {WEEKDAYS.map(w => (
            <div key={w} className="team-cal-weekday" style={{
              padding: '10px',
              textAlign: 'center',
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--symbolica-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              {w}
            </div>
          ))}
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gridAutoRows: 'minmax(110px, auto)'
        }}>
          {grid.map((d, idx) => {
            const inMonth = d.getMonth() === viewDate.getMonth();
            const isToday = d.getTime() === today.getTime();
            const key = isoLocalDate(d);
            const dayEvents = eventsByDate[key] || [];
            return (
              <div
                key={idx}
                className="team-cal-cell"
                style={{
                  borderRight: (idx % 7 !== 6) ? '1px solid #e5e7eb' : 'none',
                  borderBottom: idx < 35 ? '1px solid #e5e7eb' : 'none',
                  padding: '6px',
                  background: inMonth ? 'white' : '#fafafa',
                  opacity: inMonth ? 1 : 0.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  minWidth: 0,
                  minHeight: 0
                }}
              >
                <div style={{
                  fontSize: '12px',
                  fontWeight: isToday ? 700 : 500,
                  color: isToday ? '#fff' : 'var(--symbolica-black)',
                  background: isToday ? '#6366f1' : 'transparent',
                  borderRadius: '999px',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'flex-start'
                }}>
                  {d.getDate()}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                  {dayEvents.map((ev, i) => {
                    const isHoliday = ev.type === 'holiday';
                    return (
                      <div
                        key={`${ev.type || 'pto'}-${ev.name}-${ev.start_date}-${i}`}
                        title={isHoliday
                          ? `${ev.name} holiday: ${ev.start_date}`
                          : `${ev.name}: ${ev.start_date} \u2192 ${ev.end_date}`}
                        style={{
                          background: isHoliday ? '#EAEFF7' : colorForName(ev.name),
                          color: isHoliday ? '#3f4b66' : 'white',
                          border: isHoliday ? '1px solid #cbd5e8' : 'none',
                          fontSize: '11px',
                          fontWeight: 500,
                          padding: '2px 6px',
                          borderRadius: '3px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          minWidth: 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        {isHoliday && <span aria-hidden="true">&#127881;</span>}
                        {ev.name}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Mobile tweaks: tighten padding/typography so 7 columns still fit on
          narrow screens. The previous version hard-coded a 600px inner width
          and relied on horizontal scrolling, which the surrounding layout
          clipped, hiding the right edge of the calendar on phones. */}
      <style>{`
        @media (max-width: 600px) {
          .team-cal-weekday { padding: 6px 2px !important; font-size: 10px !important; }
          .team-cal-cell { padding: 3px !important; }
          .team-cal-cell > div:first-child {
            font-size: 11px !important; width: 20px !important; height: 20px !important;
          }
          .team-cal-cell [title] { padding: 1px 4px !important; font-size: 10px !important; }
        }
      `}</style>

      <div style={{ marginTop: '24px' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '12px', color: 'var(--symbolica-black)' }}>
          Out this month {loading && <span style={{ fontSize: '12px', color: 'var(--symbolica-muted)', fontWeight: 400 }}>(loading)</span>}
        </h2>
        {monthSummary.length === 0 && !loading && (
          <div style={{ color: 'var(--symbolica-muted)', fontSize: '14px' }}>
            No approved time off this month.
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {monthSummary.map(({ name, events: evs }) => (
            <div key={name} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '14px',
              flexWrap: 'wrap'
            }}>
              <span style={{
                display: 'inline-block',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: colorForName(name),
                flexShrink: 0
              }} />
              <span style={{ fontWeight: 500, minWidth: '100px', flexShrink: 0 }}>{name}</span>
              <span style={{ color: 'var(--symbolica-muted)' }}>
                {evs.map(e => formatRange(e.start_date, e.end_date)).join(', ')}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const navBtnStyle = {
  background: 'white',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  padding: '6px 10px',
  cursor: 'pointer',
  fontSize: '14px',
  color: 'var(--symbolica-black)'
};

function formatRange(startIso, endIso) {
  const start = parseLocalDate(startIso);
  const end = parseLocalDate(endIso);
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const sameDay = startIso === endIso;
  const opts = { month: 'short', day: 'numeric' };
  if (sameDay) return start.toLocaleDateString(undefined, opts);
  if (sameMonth) {
    return `${start.toLocaleDateString(undefined, opts)} \u2013 ${end.getDate()}`;
  }
  return `${start.toLocaleDateString(undefined, opts)} \u2013 ${end.toLocaleDateString(undefined, opts)}`;
}
